import { fetchAuthSession, getCurrentUser, signOut } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { deriveRole, parseGroups, type Role } from './roles';

export type AuthState =
  | { status: 'loading' }
  | { status: 'signed-out' }
  // `role` is COSMETIC (derived from the client-readable `cognito:groups` claim) — UI labels +
  // affordance visibility only. The server 403 / pricing-field stripping stays the real gate.
  | { status: 'signed-in'; email: string; groups: string[]; role: Role };

type AuthContextValue = {
  state: AuthState;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Wraps `getCurrentUser` + `fetchAuthSession` and subscribes to Amplify's Hub
 * auth events so the whole app sees sign-in / sign-out state flip
 * immediately. Tokens themselves are not stored in state — callers that need
 * the id token call `fetchAuthSession()` directly to always get the latest
 * (silently-refreshed) value.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  const refresh = useCallback(async () => {
    try {
      const user = await getCurrentUser();
      const session = await fetchAuthSession();
      const payload = session.tokens?.idToken?.payload;
      const email =
        (payload?.email as string | undefined) ?? user.signInDetails?.loginId ?? user.username;
      const groups = parseGroups(payload?.['cognito:groups']);
      setState({ status: 'signed-in', email, groups, role: deriveRole(groups) });
    } catch {
      setState({ status: 'signed-out' });
    }
  }, []);

  useEffect(() => {
    void refresh();
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (
        payload.event === 'signedIn' ||
        payload.event === 'tokenRefresh' ||
        payload.event === 'signedOut'
      ) {
        void refresh();
      }
    });
    return () => {
      unsubscribe();
    };
  }, [refresh]);

  const value = useMemo<AuthContextValue>(
    () => ({
      state,
      refresh,
      signOut: async () => {
        await signOut();
        setState({ status: 'signed-out' });
      },
    }),
    [state, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/**
 * Resolve the current Cognito ID token. Used by the REST API client to inject
 * `Authorization: Bearer <idToken>` — matches the rest-api middleware's
 * `tokenUse: 'id'` expectation.
 */
export async function getIdToken(): Promise<string | undefined> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString();
  } catch {
    return undefined;
  }
}
